//import BigNum from "./BigNum";
import ManExp from "./ManExp";


const MAX_SIGNIFICANT_DIGITS = 17; // Highest value you can safely put here is Number.MAX_SAFE_INTEGER-MAX_SIGNIFICANT_DIGITS

const EXP_LIMIT = 9e15; // The largest exponent that can appear in a Number, though not all mantissas are valid here.

const NUMBER_EXP_MAX = 308; // The smallest exponent that can appear in a Number, though not all mantissas are valid here.

const NUMBER_EXP_MIN = -324; // Tolerance which is used for Number conversion to compensate floating-point error.

const ROUND_TOLERANCE = 1e-10;

// We need this lookup table because Math.pow(10, exponent)
// when exponent's absolute value is large is slightly inaccurate.
// You can fix it with the power of math... or just make a lookup table.
// Faster AND simpler
const POWERS_OF_10 = [];
for (let i = NUMBER_EXP_MIN + 1; i <= NUMBER_EXP_MAX; i++) {
    POWERS_OF_10.push(Number("1e" + i));
}


export default class BigNumMath {
    constructor() {
    }

    /**
     * @param {number} power 
     * @returns {number}
     */
    static powerOf10(power) {
        const indexOf0InPowersOf10 = 323;
        return POWERS_OF_10[power + indexOf0InPowersOf10];
    }

    /**
     * @param {ManExp} out
     * @param {number} value 
     */
    static fromNumber(out, value) {
        // SAFETY: Handle Infinity and NaN in a somewhat meaningful way.
        if (isNaN(value)) {
            out.m = Number.NaN;
            out.e = Number.NaN;
        } else if (value === Number.POSITIVE_INFINITY) {
            out.m = 1;
            out.e = EXP_LIMIT;
        } else if (value === Number.NEGATIVE_INFINITY) {
            out.m = -1;
            out.e = EXP_LIMIT;
        } else if (value === 0) {
            out.m = 0;
            out.e = 0;
        } else {
            out.e = Math.floor(Math.log10(Math.abs(value))); // SAFETY: handle 5e-324, -5e-324 separately

            out.m = out.e === NUMBER_EXP_MIN ? value * 10 / 1e-323 : value / BigNumMath.powerOf10(out.e); // SAFETY: Prevent weirdness.

            BigNumMath.normalize(out);
        }

        return out;
    }

    /**
     * @param {ManExp} out 
     * @returns {ManExp}
     */
    static negate(out) {
        if (typeof value === "number") {
            console.log('BigNumMath.negate() passed number instead of BigNum');
            console.trace();
        }

        out.m = -out.m;
        return out;
    }

    /**
     * @param {ManExp} out 
     * @param {ManExp} value
     * @returns {ManExp}
     */
    static copy(out, value) {
        if (typeof value === "number") {
            console.log('BigNumMath.copy() passed number instead of BigNum');
            console.trace();
        }

        out.m = value.m;
        out.e = value.e;
        return out;
    }

    /**
     * @param {ManExp} out
     * @returns {ManExp}
     */
    static normalize(out) {
        if (out.m >= 1 && out.m < 10) {
            return out;
        }

        if (out.m === 0) {
            out.m = 0;
            out.e = 0;
            return out;
        }

        const tempExponent = Math.floor(Math.log10(Math.abs(out.m)));
        out.m = tempExponent === NUMBER_EXP_MIN ? out.m * 10 / 1e-323 : out.m / BigNumMath.powerOf10(tempExponent);
        out.e += tempExponent;
        return out;
    }

    ///////////////////////////////////////////
    // Arithmetic
    ///////////////////////////////////////////

    /**
     * @param {ManExp} out 
     * @param {ManExp} value 
     * @returns {ManExp}
     */
    static multiply(out, value) {
        if (typeof value === "number") {
            console.log('BigNumMath.multiply() passed number instead of BigNum');
            console.trace();
        }

        out.m *= value.m;
        out.e += value.e;
        return BigNumMath.normalize(out);
    }

    /**
     * @param {ManExp} out 
     * @param {ManExp} value 
     * @returns {ManExp}
     */
    static add(out, value) {
        if (typeof value === "number") {
            console.log('BigNumMath.add() passed number instead of BigNum');
            console.trace();
        }

        // figure out which is bigger, shrink the mantissa of the smaller
        // by the difference in exponents, add mantissas, normalize and return

        if (out.m === 0) {
            out.m = value.m;
            out.e = value.e;
            return out;
        }

        if (value.m === 0) {
            return out;
        }

        let biggerM;
        let biggerE;
        let smallerM;
        let smallerE;
        if (out.e >= value.e) {
            biggerM = out.m;
            biggerE = out.e;
            smallerM = value.m;
            smallerE = value.e;
        }
        else {
            biggerM = value.m;
            biggerE = value.e;
            smallerM = out.m;
            smallerE = out.e;
        }

        // Have to do this because adding numbers that were once integers but scaled down is imprecise.
        if (biggerE - smallerE > MAX_SIGNIFICANT_DIGITS) {
            out.m = biggerM;
            out.e = biggerE;
            return out;
        }

        const mantissa = Math.round(1e14 * biggerM + 1e14 * smallerM * BigNumMath.powerOf10(smallerE - biggerE));

        out.m = mantissa;
        out.e = biggerE - 14;

        // We need to normalize the result now
        return BigNumMath.normalize(out);
    }

    ///////////////////////////////////////////
    // pow and log
    ///////////////////////////////////////////

    /**
     * @param {ManExp} value 
     * @returns {number}
     */
    static log10(value) {
        if (typeof value === "number") {
            console.log('BigNumMath.log10() passed number instead of BigNum');
            console.trace();
        }

        return value.e + Math.log10(value.m);
    }
  
    /**
     * @param {ManExp} value 
     * @returns {number}
     */
    static log2(value) {
        return 3.321928094887362 * BigNumMath.log10(value);
    }
  
    /**
     * @param {ManExp} value 
     * @returns {number}
     */
    static ln(value) {
        return 2.302585092994045 * BigNumMath.log10(value);
    }

    /**
     * @param {ManExp} value 
     * @param {number} base 
     * @returns {number}
     */
    static log(value, base) {
        return Math.LN10 / Math.log(base) * BigNumMath.log10(value);
    }

    /**
     * @param {ManExp} out
     * @param {number} value 
     * @returns {ManExp}
     */
    static pow10(out, value) {
        if (Number.isInteger(value)) {
            out.set(1, value);
            return out;
        }

        out.set(Math.pow(10, value % 1), Math.trunc(value));
        return BigNumMath.normalize(out);
    }

    /**
     * @param {ManExp} out
     * @param {number} value 
     * @returns {ManExp}
     */
    static pow(out, value) {
        const numberValue = value;

        const temp = out.e * numberValue;
        let newMantissa;

        if (Number.isSafeInteger(temp)) {
            newMantissa = Math.pow(out.m, numberValue);

            if (isFinite(newMantissa) && newMantissa !== 0) {
                out.m = newMantissa;
                out.e = temp;
                return BigNumMath.normalize(out);
            }
        }

        const newExponent = Math.trunc(temp);
        const residue = temp - newExponent;
        newMantissa = Math.pow(10, numberValue * Math.log10(out.m) + residue);

        if (isFinite(newMantissa) && newMantissa !== 0) {
            out.m = newMantissa;
            out.e = newExponent;
            return BigNumMath.normalize(out);
        }

        // Update out to be pow10
        BigNumMath.pow10(out, numberValue * BigNumMath.absLog10(out)); // this is 2x faster and gives same values AFAIK

        if (BigNumMath.sign(out) === -1) {
            if (Math.abs(numberValue % 2) === 1) {
                return BigNumMath.negate(out);
            } else if (Math.abs(numberValue % 2) === 0) {
                return out; // Out was already normalized in the pow10 call.
            }

            out.m = Number.NaN;
            out.e = Number.NaN;
            return out;
        }

        return out; // Out was already normalized in the pow10 call.
    }

    ///////////////////////////////////////////
    // Conversions
    ///////////////////////////////////////////

    /**
     * @param {ManExp} value
     * @returns {string}
     */
    static toString(value) {
        if (typeof value === "number") {
            console.log('BigNumMath.toString() passed number instead of BigNum');
            console.trace();
        }

        if (isNaN(value.m) || isNaN(value.e)) {
            return "NaN";
        }

        if (value.e >= EXP_LIMIT) {
            return value.m > 0 ? "Infinity" : "-Infinity";
        }

        if (value.e <= -EXP_LIMIT || value.m === 0) {
            return "0";
        }

        if (value.e < 21 && value.e > -7) {
            return BigNumMath.toNumber(value).toString();
        }

        return value.m + "e" + (value.e >= 0 ? "+" : "") + value.e;
    }

    /**
     * @param {ManExp} value
     * @returns {number}
     */
    static toNumber(value) {
        if (typeof value === "number") {
            console.log('BigNumMath.toNumber() passed number instead of BigNum');
            console.trace();
        }

        if (!isFinite(value.e)) {
            return Number.NaN;
        }

        if (value.e > NUMBER_EXP_MAX) {
            return value.m > 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
        }

        if (value.e < NUMBER_EXP_MIN) {
            return 0;
        } // SAFETY: again, handle 5e-324, -5e-324 separately


        if (value.e === NUMBER_EXP_MIN) {
            return value.m > 0 ? 5e-324 : -5e-324;
        }

        const result = value.m * BigNumMath.powerOf10(value.e);

        if (!isFinite(result) || value.e < 0) {
            return result;
        }

        const resultRounded = Math.round(result);

        if (Math.abs(resultRounded - result) < ROUND_TOLERANCE) {
            return resultRounded;
        }

        return result;
    }

    /**
     * @param {ManExp} value
     * @param {number} places 
     * @returns {string}
     */
    static toExponential(value, places) {
        if (typeof value === "number") {
            console.log('BigNumMath.toExponential() passed number instead of BigNum');
            console.trace();
        }

        if (isNaN(value.m) || isNaN(value.e)) {
            return "NaN";
        }

        if (value.e >= EXP_LIMIT) {
            return value.m > 0 ? "Infinity" : "-Infinity";
        }

        if (value.e <= -EXP_LIMIT || value.m === 0) {
            return "0" + (places > 0 ? BigNumMath.padEnd(".", places + 1, "0") : "") + "e+0";
        }
        // two cases:
        // 1) exponent is < 308 and > -324: use basic toFixed
        // 2) everything else: we have to do it ourselves!

        if (value.e > NUMBER_EXP_MIN && value.e < NUMBER_EXP_MAX) {
            return BigNumMath.toNumber(value).toExponential(places);
        }

        if (!isFinite(places)) {
            places = MAX_SIGNIFICANT_DIGITS;
        }

        const len = places + 1;
        const numDigits = Math.max(1, Math.ceil(Math.log10(Math.abs(value.m))));
        const rounded = Math.round(value.m * Math.pow(10, len - numDigits)) * Math.pow(10, numDigits - len);
        return rounded.toFixed(Math.max(len - numDigits, 0)) + "e" + (value.e >= 0 ? "+" : "") + value.e;
    }

    /**
     * @param {ManExp} value
     * @param {number} places 
     * @returns {string}
     */
    static toFixed(value, places) {
        if (typeof value === "number") {
            console.log('BigNumMath.toFixed() passed number instead of BigNum');
            console.trace();
        }

        if (isNaN(value.m) || isNaN(value.e)) {
            return "NaN";
        }

        if (value.e >= EXP_LIMIT) {
            return value.m > 0 ? "Infinity" : "-Infinity";
        }

        if (value.e <= -EXP_LIMIT || value.m === 0) {
            return "0" + (places > 0 ? BigNumMath.padEnd(".", places + 1, "0") : "");
        }

        // two cases:
        // 1) exponent is 17 or greater: just print out mantissa with the appropriate number of zeroes after it
        // 2) exponent is 16 or less: use basic toFixed

        if (value.e >= MAX_SIGNIFICANT_DIGITS) {
            return value.m.toString().replace(".", "").padEnd(value.e + 1, "0") + (places > 0 ? BigNumMath.padEnd(".", places + 1, "0") : "");
        }

        return BigNumMath.toNumber(value).toFixed(places);
    }

    /**
     * @param {ManExp} value
     * @param {number} places 
     * @returns {string}
     */
    static toPrecision(value, places) {
        if (typeof value === "number") {
            console.log('BigNumMath.toPrecision() passed number instead of BigNum');
            console.trace();
        }

        if (value.e <= -7) {
            return BigNumMath.toExponential(value, places - 1);
        }

        if (places > value.e) {
            return BigNumMath.toFixed(value, places - value.e - 1);
        }

        return BigNumMath.toExponential(value, places - 1);
    }

    /**
     * @param {ManExp} value 
     * @returns {string}
     */
    static valueOf(value) {
        return BigNumMath.toString(value);
    }

    /**
     * @param {ManExp} value 
     * @returns {string}
     */
    static toJSON(value) {
        return BigNumMath.toString(value);
    }

    /**
     * @param {ManExp} value 
     * @returns {string}
     */
    static toStringWithDecimalPlaces(value, places) {
        return BigNumMath.toExponential(value, places);
    }

    //   Decimal.prototype.abs = function () {
    //     return ME_NN(Math.abs(this.m), this.e);
    //   };

    /**
     * @param {BigNum} out 
     * @returns {BigNum}
     */
    static abs(out) {
        if (typeof value === "number") {
            console.log('BigNumMath.abs() passed number instead of BigNum');
            console.trace();
        }

        out.m = Math.abs(out.m);
        return out;
    }
 
    /**
     * @param {ManExp} value 
     * @returns {number}
     */
    static sign(value) {
        if (typeof value === "number") {
            console.log('BigNumMath.sign() passed number instead of BigNum');
            console.trace();
        }

        return Math.sign(value.m);
    }


    ///////////////////////////////////////////
    // Round/Floor/Ceil/Trunc
    ///////////////////////////////////////////    

    /**
     * @param {ManExp} out 
     * @returns {ManExp}
     */
    static round(out) {
        if (out.e < -1) {
            return out.setZero();
        }

        if (out.e < MAX_SIGNIFICANT_DIGITS) {
            return BigNumMath.fromNumber(out, Math.round(BigNumMath.toNumber(out)));
        }

        return out;
    }

    /**
     * @param {ManExp} out 
     * @returns {ManExp}
     */
    static floor(out) {
        if (out.e < -1) {
            if (Math.sign(out.m) >= 0) {
                return out.setZero();
            }
            else {
                return BigNumMath.fromNumber(out, -1);
            }
        }

        if (out.e < MAX_SIGNIFICANT_DIGITS) {
            return BigNumMath.fromNumber(out, Math.floor(BigNumMath.toNumber(out)));
        }

        return out;
    }

    /**
     * @param {ManExp} out 
     * @returns {ManExp}
     */
    static ceil(out) {
        if (out.e < -1) {
            if (Math.sign(out.m) > 0) {
                return BigNumMath.fromNumber(out, 1);
            }
            else {
                return out.setZero();
            }
        }

        if (out.e < MAX_SIGNIFICANT_DIGITS) {
            return BigNumMath.fromNumber(out, Math.ceil(BigNumMath.toNumber(out)));
        }

        return out;
    }

    /**
     * @param {ManExp} out 
     * @returns {ManExp}
     */
    static trunc(out) {
        if (out.e < 0) {
            return out.setZero();
        }

        if (out.e < MAX_SIGNIFICANT_DIGITS) {
            return BigNumMath.fromNumber(out, Math.trunc(BigNumMath.toNumber(out)));
        }

        return out;
    }
  

    ///////////////////////////////////////////
    // Comparisons
    ///////////////////////////////////////////

    /**
     * @param {ManExp} out
     * @returns {ManExp}
     */
    static recip(out) {
        out.m = 1 / out.m;
        out.e = -out.e;
        return BigNumMath.normalize(out);
    }

    /**
     * @param {ManExp} value
     * @returns {number}
     */
    static absLog10(value) {
        return value.e + Math.log10(Math.abs(value.m));
    }

    /**
     * @private
     * @param {string} string 
     * @param {number} maxLength 
     * @param {string} fillString 
     * @returns {string}
     */
    static padEnd(string, maxLength, fillString) {

        if (string == null || maxLength == null) {
            return string;
        }

        const result = String(string);
        const targetLen = typeof maxLength === 'number'
            ? maxLength
            : parseInt(maxLength, 10);

        if (isNaN(targetLen) || !isFinite(targetLen)) {
            return result;
        }

        const length = result.length;
        if (length >= targetLen) {
            return result;
        }

        let filled = fillString == null ? '' : String(fillString);
        if (filled === '') {
            filled = ' ';
        }

        const fillLen = targetLen - length;

        while (filled.length < fillLen) {
            filled += filled;
        }

        const truncated = filled.length > fillLen ? filled.substr(0, fillLen) : filled;

        return result + truncated;
    }

}